import React, { useCallback, useEffect, useRef, useState } from "react"
import { Box } from "@mui/material"
import { measureRegion } from "../utils/imageFunctions"

// Placements from /api/admin/hold-color-mapping carry square_x / square_y in
// the pixel space of the 1024x1024 kilter images, so a touch on the board
// image maps to square space with a single scale factor.
const SQUARE_SIZE = 1024

export const findNearestPlacement = (placements, sx, sy) => {
  let best = null
  let bestDist = Infinity
  for (const p of placements) {
    const dx = p.square_x - sx
    const dy = p.square_y - sy
    const d2 = dx * dx + dy * dy
    if (d2 < bestDist) {
      bestDist = d2
      best = p
    }
  }
  return best
}

/**
 * The hold nearest a viewport point on `boardElement`, or null when the
 * point is outside the board (so a drag can leave the image to cancel).
 */
export const placementAtClientPoint = (
  boardElement,
  placements,
  clientX,
  clientY
) => {
  if (!boardElement || !placements.length) {
    return null
  }
  const rect = boardElement.getBoundingClientRect()
  if (
    clientX < rect.left ||
    clientX > rect.right ||
    clientY < rect.top ||
    clientY > rect.bottom
  ) {
    return null
  }
  const sx = ((clientX - rect.left) / rect.width) * SQUARE_SIZE
  const sy = ((clientY - rect.top) / rect.height) * SQUARE_SIZE
  return findNearestPlacement(placements, sx, sy)
}

/**
 * Board image with rings drawn over holds. Sizes itself to the largest
 * square that fits its parent. Pointer handling lives in the page so a
 * drag can start outside the board (e.g. on a hold-type button); hit-test
 * with `placementAtClientPoint` against `boardRef`.
 *
 * `placements` — hold positions in square space ({placement_id, square_x,
 *   square_y, ...}).
 * `selected` — Map of placement_id -> rgb triple for committed holds.
 * `candidate` — placement currently under the finger (or null).
 * `candidateColor` — rgb triple for the candidate ring.
 */
export default function TouchBoard({
  boardRef,
  placements,
  selected,
  candidate,
  candidateColor = [255, 255, 255],
}) {
  const localRef = useRef(null)
  const containerRef = boardRef || localRef
  const canvasRef = useRef(null)
  const holdsImageDataRef = useRef(null)
  const regionCacheRef = useRef(new Map())
  const [holdsLoaded, setHoldsLoaded] = useState(false)
  const [size, setSize] = useState(0)

  // Rasterize the hold mask once so rings can be sized to each hold.
  useEffect(() => {
    const image = new Image()
    image.src = "/images/kilter-holds.png"
    image.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = image.width
      canvas.height = image.height
      const ctx = canvas.getContext("2d")
      ctx.drawImage(image, 0, 0)
      holdsImageDataRef.current = ctx.getImageData(
        0,
        0,
        canvas.width,
        canvas.height
      )
      setHoldsLoaded(true)
    }
  }, [])

  // Fit the largest square into the parent box.
  useEffect(() => {
    const parent = containerRef.current?.parentElement
    if (!parent) {
      return
    }
    const measure = () =>
      setSize(Math.floor(Math.min(parent.clientWidth, parent.clientHeight)))
    measure()
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure)
      return () => window.removeEventListener("resize", measure)
    }
    const observer = new ResizeObserver(measure)
    observer.observe(parent)
    return () => observer.disconnect()
  }, [containerRef])

  // Ring geometry for a placement in square space, cached per hold since
  // measureRegion flood-fills the mask.
  const ringFor = useCallback((placement) => {
    const cache = regionCacheRef.current
    if (cache.has(placement.placement_id)) {
      return cache.get(placement.placement_id)
    }
    const imageData = holdsImageDataRef.current
    const region = imageData
      ? measureRegion(imageData, placement.square_x, placement.square_y)
      : null
    const ring = {
      center: region?.center || [placement.square_x, placement.square_y],
      radius: region
        ? Math.min(Math.max(Math.sqrt(region.area / Math.PI) + 8, 14), 55)
        : 18,
    }
    cache.set(placement.placement_id, ring)
    return ring
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !size) {
      return
    }
    canvas.width = size
    canvas.height = size
    const scale = size / SQUARE_SIZE
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, size, size)

    const drawRing = (placement, rgb, width) => {
      const { center, radius } = ringFor(placement)
      ctx.beginPath()
      ctx.arc(center[0] * scale, center[1] * scale, radius * scale, 0, 2 * Math.PI)
      ctx.strokeStyle = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
      ctx.lineWidth = width * scale
      ctx.stroke()
    }

    const byId = new Map(placements.map((p) => [p.placement_id, p]))
    selected.forEach((rgb, id) => {
      const placement = byId.get(id)
      if (placement) {
        drawRing(placement, rgb, 6)
      }
    })
    if (candidate) {
      // Double ring so the candidate reads clearly even over a selected hold
      drawRing(candidate, [0, 0, 0], 12)
      drawRing(candidate, candidateColor, 7)
    }
  }, [
    placements,
    selected,
    candidate,
    candidateColor,
    holdsLoaded,
    size,
    ringFor,
  ])

  return (
    <Box
      ref={containerRef}
      data-testid="touch-board"
      onContextMenu={(event) => event.preventDefault()}
      sx={{
        position: "relative",
        width: size || "100%",
        height: size || "auto",
        aspectRatio: "1/1",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        cursor: "crosshair",
      }}
    >
      <img
        src="/images/kilter.jpg"
        alt="kilter board"
        draggable={false}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "transparent",
          pointerEvents: "none",
        }}
      />
    </Box>
  )
}
